/**
 *
 * SummaryItem
 *
 */

import React, { memo, FunctionComponent } from 'react';
import Badge from '../../../../components/Badge/Badge.component';
import { StyledCard } from '../../../../global-styles';
import { StyledSummaryItem } from './styledComponents';
import usersIcon from '../../../../assets/icons/users.svg';
import awardIcon from '../../../../assets/icons/award.svg';
import timesIcon from '../../../../assets/icons/times.svg';
import flagIcon from '../../../../assets/icons/flag.svg';
import useResizeObserver from 'use-resize-observer';
import { Link } from 'react-router-dom';
import { useQueryParams } from 'use-query-params';
import { AppQueryParamsConfigMap, TABLET_BREAKPOINT } from '../../../../app/constants';
import { useEventFeed } from '../../../../api/useEventFeed';
import { HAS_NO_RESULTS_DISCIPLINES, HAS_NO_TIMES_DISCIPLINES } from '../../constants';
import enterIcon from '../../../../assets/icons/enter.svg';
import useChangeLocationSearch from '../../../../hooks/useChangeLocationSearch';
import useWindowSize from '../../../../hooks/useWindowSize';

interface IProps {
  id: number;
}

const SummaryItem: FunctionComponent<IProps> = ({ id }) => {
  const { width: windowWidth = document.body.clientWidth } = useWindowSize();
  const { ref, width = windowWidth } = useResizeObserver();

  const [query] = useQueryParams(AppQueryParamsConfigMap);
  const { data: activeEvent } = useEventFeed({ eventId: query.e });

  const classItem = activeEvent?.eventClassList.find((item) => item.id === id);

  const activeItem = classItem?.id === Number(query.c);

  const hasResults =
    activeEvent && !HAS_NO_RESULTS_DISCIPLINES.includes(activeEvent.discipline);

  const hasTimes =
    activeEvent && !HAS_NO_TIMES_DISCIPLINES.includes(activeEvent.discipline);

  const getModifiedSearchQuery = useChangeLocationSearch();

  if (width < TABLET_BREAKPOINT) {
    return (
      <StyledSummaryItem ref={ref} hasActiveItem={!!query.c} className="mb-10">
        <StyledCard className={activeItem ? 'active' : ''}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-start mb-15 class-item-header flex-wrap">
              {classItem?.startTime && classItem?.endTime ? (
                <div className="mb-20">
                  <div className="txt fw-7">
                    {classItem?.startTime} - {classItem?.endTime}
                  </div>
                  <div className="txt txt-secondary fs-11">Class Time</div>
                </div>
              ) : (
                <div className="d-flex mb-10">
                  <Badge className="no-border" icon={<img src={usersIcon} alt="" />}>
                    {classItem?.numberOfEntries}
                  </Badge>
                  {classItem?.liveEventFeedStatusText === 'Closed' && (
                    <Badge className="no-border mr-10">
                      {classItem?.liveEventFeedStatusText}
                    </Badge>
                  )}
                </div>
              )}
              <div className="d-flex">
                {hasTimes && (
                  <Link
                    to={{
                      pathname: '/feed/times',
                      search: getModifiedSearchQuery([
                        { param: 'c', value: classItem?.id.toString() },
                      ]),
                    }}
                    className="mr-10"
                  >
                    <Badge className="grey-accent" icon={<img src={timesIcon} alt="" />}>
                      Times
                    </Badge>
                  </Link>
                )}
                {hasResults && (
                  <Link
                    to={{
                      pathname: '/feed/results',
                      search: getModifiedSearchQuery([
                        { param: 'e', value: classItem?.eventId.toString() },
                        { param: 'c', value: classItem?.id.toString() },
                      ]),
                    }}
                    className="mr-10"
                  >
                    <Badge className="grey-accent" icon={<img src={awardIcon} alt="" />}>
                      Results
                    </Badge>
                  </Link>
                )}
                {classItem?.liveEventFeedStatusText === 'Open' && (
                  <a
                    href={`https://myridinglife.com/smartlogin.aspx?s=${classItem?.eventId}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Badge accent active icon={<img src={enterIcon} alt="" />}>
                      Enter
                    </Badge>
                  </a>
                )}
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-15">
              {classItem?.arenaName && (
                <div className="d-flex align-items-center">
                  <div className="icon mr-8">
                    <img src={flagIcon} alt="" />
                  </div>
                  <div className="live-news-time-lbl mb-0">
                    {classItem?.arenaName}
                  </div>
                </div>
              )}
              {classItem?.startTime && classItem?.endTime && (
                <Badge className="no-border" icon={<img src={usersIcon} alt="" />}>
                  {classItem?.numberOfEntries}
                </Badge>
              )}
            </div>

            <div className="d-flex align-items-start">
              <div className="mr-8 txt fw-7">{classItem?.clNumber}</div>
              <div className="mr-8 txt fw-7">{classItem?.clSort}</div>
              <div>
                <div className="txt fw-7 name mb-10">
                {classItem?.className}
                </div>
                <div className="txt txt-secondary fs-11">
                  <p>{classItem?.classDay}</p>
                  <p>{classItem?.className1}</p>
                  <p>{classItem?.className2}</p>
                  <p>{classItem?.judges}</p>
                </div>
              </div>
            </div>
          </div>
        </StyledCard>
      </StyledSummaryItem>
    );
  }
  return (
    <StyledSummaryItem
      ref={ref}
      hasActiveItem={!!query.c}
      className="d-table-row"
    >
      <div className="mr-24 txt fw-7 cl-number-cell d-table-cell">
        {classItem?.clNumber}
      </div>
      <div className="mr-24 txt fw-7 cl-number-cell d-table-cell">
        {classItem?.clSort}
      </div>
      <div className="name-cell mr-24 d-table-cell">
        <div className="txt fw-7 name">{classItem?.className1}</div>
      </div>
      <div className="mr-24 name descr-cell d-table-cell">
        <div className="txt txt-secondary fs-11">
          <p>{classItem?.className2}</p>
          {classItem?.judges && (
            <p className="mt-8">Judges: {classItem?.judges}</p>
          )}
        </div>
      </div>
      <div className="mr-24 txt fw-7 cl-number-cell d-table-cell">
        {classItem?.classDay}
      </div>
      <div className="mr-24 date-cell d-table-cell">
        {classItem?.startTime && classItem?.endTime && (
          <>
            <div className="txt fw-7">
              {classItem?.startTime} - {classItem?.endTime}
            </div>
            <div className="txt txt-secondary fs-11">Class Time</div>
          </>
        )}
      </div>
      <div className="mr-24 arena-cell d-table-cell">
        {classItem?.arenaName && (
          <div className="d-flex align-items-center">
            <div className="icon mr-8">
              <img src={flagIcon} alt="" />
            </div>
            <div className="live-news-time-lbl mb-0">
              {classItem?.arenaName}
            </div>
          </div>
        )}
      </div>
      <div className="mr-24 d-table-cell">
        <Badge className="no-border" icon={<img src={usersIcon} alt="" />}>
          {classItem?.numberOfEntries}
        </Badge>
      </div>
      <div className="d-table-cell">
        <div className="d-flex align-items-center justify-content-end">
          {classItem?.liveEventFeedStatusText === 'Closed' && (
            <div className="mr-10">
              <Badge className="no-border">{classItem?.liveEventFeedStatusText}</Badge>
            </div>
          )}
          {hasTimes && (
            <Link
              className="mr-10"
              to={{
                pathname: '/feed/times',
                search: getModifiedSearchQuery([
                  { param: 'c', value: classItem?.id.toString() },
                ]),
              }}
            >
              <Badge className="grey-accent" icon={<img src={timesIcon} alt="" />}>
                Times
              </Badge>
            </Link>
          )}
          {hasResults && (
            <Link
              className="mr-10"
              to={{
                pathname: '/feed/results',
                search: getModifiedSearchQuery([
                  { param: 'e', value: classItem?.eventId.toString() },
                  { param: 'c', value: classItem?.id.toString() },
                ]),
              }}
            >
              <Badge className="grey-accent" icon={<img src={awardIcon} alt="" />}>
                Results
              </Badge>
            </Link>
          )}
          {classItem?.liveEventFeedStatusText === 'Open' && (
            <a
              href={`https://myridinglife.com/smartlogin.aspx?s=${classItem?.eventId}`}
              target="_blank"
              rel="noreferrer"
            >
              <Badge accent active icon={<img src={enterIcon} alt="" />}>
                Enter
              </Badge>
            </a>
          )}
        </div>
      </div>
    </StyledSummaryItem>
  );
};

export default memo(SummaryItem);
